import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import CustomersExtraPresentational from './CustomersExtraPresentational';
import clientsAPIs from '../../lib/fetch/clientsApi';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const CustomersExtra = ({ profile }) => {
  const [customersExtra, setCustomersExtra] = useState([]);

  const [total, setTotal] = useState(0);
  const [{ newToken, newIsAdmin } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    profileId: withDefault(StringParam, ''),
    referenceId: withDefault(StringParam, ''),
    businessName: withDefault(StringParam, ''),
    taxCode: withDefault(StringParam, ''),
    office: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    profileId,
    referenceId,
    businessName,
    taxCode,
    office,
  } = query;

  useEffect(() => {
    fetchCustomersExtra();
  }, [limit, offset, profileId, referenceId, businessName, taxCode, office]);

  const fetchCustomersExtra = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/customer-extra`,
      {
        headers: { Authorization: newToken },
        params: {
          limit,
          offset,
          businessName,
          taxCode,
          office,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;
    setCustomersExtra(data);
    setTotal(+headers['x-total-count']);
  };

  function onFilter(queryDiff) {
    if (
      (queryDiff.businessName && queryDiff.businessName !== businessName) || // first search
      (businessName && queryDiff.businessName === '') || // search deleted
      (queryDiff.taxCode && queryDiff.taxCode !== taxCode) || // first search
      (taxCode && queryDiff.taxCode === '') || // search deleted
      (queryDiff.office && queryDiff.office !== office) || // first search
      (office && queryDiff.office === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const createCustomerExtra = async (data) => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/customer-extra`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchCustomersExtra();
  };

  const updateCustomerExtra = async (data, id) => {
    showLoading();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/customer-extra/${id}`,
      data,
      {
        headers: { Authorization: newToken },
      }
    );
    hideLoading();

    if (response instanceof Error) {
      const message = JSON.parse(response?.request?.response).message;
      return alert.error(message || t('COMMON:GENERIC_ERROR'));
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchCustomersExtra();
  };

  const checkTaxCode = async (taxCode) => {
    showLoading();

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/customer-extra/checkTaxCode`,
      {
        taxCode: taxCode,
      },
      {
        headers: { Authorization: newToken },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      throw new Error('Errore nel controllo della partita IVA');
    }

    return response.data;
  };

  const [hasImportedCustomers, setHasImportedCustomers] = useState(false);
  const [
    customersImportFeedbackData,
    setCustomersImportFeedbackData,
  ] = useState({});
  const toggleShowImportFeedback = () => setHasImportedCustomers((v) => !v);

  const [
    hasImportedCustomersAssociation,
    setHasImportedCustomersAssociation,
  ] = useState(false);
  const [
    customersImportFeedbackDataAssociation,
    setCustomersImportFeedbackDataAssociation,
  ] = useState({});
  const toggleShowImportFeedbackAssociation = () =>
    setHasImportedCustomersAssociation((v) => !v);

  const onImportCustomers = async (file) => {
    showLoading();
    const { error, data } = await clientsAPIs.importClientsFromAdmins({
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedback();
    setCustomersImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchCustomersExtra();
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    } else {
      alert.success(t('COMMON:SUCCESS_UPLOAD'));
    }
  };

  const onImportCustomersAssociation = async (file) => {
    showLoading();
    const { error, data } = await clientsAPIs.importClientsFromAssociation({
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedbackAssociation();
    setCustomersImportFeedbackDataAssociation({
      ...data,
      validationErrors: error?.response?.data,
    });
    fetchCustomersExtra();
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    } else {
      alert.success(t('COMMON:SUCCESS_UPLOAD'));
    }
  };

  const searchOffices = async (value) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/offices`,
      {
        headers: { Authorization: newToken },
        params: { search: value },
      }
    );

    if (response instanceof Error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return [];
    }

    const { data = [] } = response;
    return data;
  };

  return (
    <CustomersExtraPresentational
      data={customersExtra}
      profile={profile}
      newIsAdmin={newIsAdmin}
      query={query}
      total={total}
      onFilter={onFilter}
      onNewCustomerExtraConfirm={createCustomerExtra}
      onCustomerExtraUpdate={updateCustomerExtra}
      checkTaxCode={checkTaxCode}
      onImportCustomers={onImportCustomers}
      onImportCustomersAssociation={onImportCustomersAssociation}
      toggleShowImportFeedback={toggleShowImportFeedback}
      customersImportFeedbackData={customersImportFeedbackData}
      hasImportedCustomers={hasImportedCustomers}
      toggleShowImportFeedbackAssociation={toggleShowImportFeedbackAssociation}
      customersImportFeedbackDataAssociation={
        customersImportFeedbackDataAssociation
      }
      hasImportedCustomersAssociation={hasImportedCustomersAssociation}
      searchOffices={searchOffices}
    />
  );
};

export default CustomersExtra;
